<template>
  <form @submit.prevent="confirmUpdate()" action="">
    <ion-row>
      <ion-col size="12">
        <ion-list>
          <ion-item v-if="validationError" lines="none">
            <ion-label className="ion-text-wrap">
              <ion-text color="danger">
                <p v-for="error of v$.$errors" :key="error.$uid">
                  <ion-icon
                    :ios="alertCircleOutline"
                    :md="alertCircleSharp"
                    size="small"
                    style="vertical-align: middle"
                  ></ion-icon
                  >{{ error.$property }}: {{ error.$message }}
                </p></ion-text
              >
            </ion-label>
          </ion-item>

          <ion-item>
            <ion-label position="floating">Username</ion-label>
            <ion-input type="text" :value="username" disabled></ion-input>
          </ion-item>

          <ion-item>
            <ion-label position="floating">Name</ion-label>
            <ion-input type="text" v-model="name" disabled></ion-input>
          </ion-item>

          <ion-item>
            <ion-label position="floating">Fathers name</ion-label>
            <ion-input type="text" v-model="fathers_name" disabled></ion-input>
          </ion-item>

          <ion-item>
            <ion-label position="floating">Mothers name</ion-label>
            <ion-input type="text" v-model="mothers_name" disabled></ion-input>
          </ion-item>

          <ion-item>
            <ion-label position="floating">Phone</ion-label>
            <ion-input type="text" v-model="phone" disabled></ion-input>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.standard.$error }">
            <ion-label position="floating">Standard</ion-label>
            <ion-select v-model="standard">
              <ion-select-option
                v-for="standard of standards"
                :key="standard.id"
                :value="standard.id"
              >
                Standard {{ standard.name }}
              </ion-select-option>
            </ion-select>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.section.$error }">
            <ion-label position="floating">Section</ion-label>
            <ion-select v-model="section">
              <ion-select-option
                v-for="section of sections"
                :key="section.id"
                :value="section.id"
              >
                Section {{ section.name }}
              </ion-select-option>
            </ion-select>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.roll_no.$error }">
            <ion-label position="floating">Roll No</ion-label>
            <ion-input type="text" v-model="roll_no"></ion-input>
          </ion-item>
        </ion-list>
      </ion-col>
    </ion-row>
    <ion-row>
      <ion-col>
        <ion-button type="submit" expand="full" class="ion-margin-vertical">
          Save
        </ion-button>
      </ion-col>
    </ion-row>
  </form>
</template>

<script>
import {
  IonRow,
  IonCol,
  IonList,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonItem,
  IonIcon,
  IonButton,
  loadingController,
  alertController,
  IonText,
  IonInput,
} from "@ionic/vue";

import { alertCircleOutline, alertCircleSharp } from "ionicons/icons";

import { required, maxValue, integer, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";

export default {
  props: {
    resource: {
      type: Object,
      required: true,
    },
  },
  emits: ["closeModal"],
  components: {
    IonRow,
    IonCol,
    IonList,
    IonLabel,
    IonSelect,
    IonSelectOption,
    IonItem,
    IonIcon,
    IonButton,
    IonText,
    IonInput,
  },
  data() {
    return {
      id: this.resource.id,
      username: this.resource.username,

      name: this.resource.user_detail.name,
      standard: null,
      section: null,
      roll_no: this.resource.student_trashed.roll_no,

      fathers_name: this.resource.user_detail.fathers_name,
      mothers_name: this.resource.user_detail.mothers_name,
      phone: this.resource.user_detail.phone,

      alertCircleOutline,
      alertCircleSharp,

      validationError: null,
      xhrError: {
        header: null,
        description: null,
      },
    };
  },

  setup() {
    return { v$: useVuelidate() };
  },

  validations() {
    return {
      standard: {
        required: helpers.withMessage("is required", required),
        integer: helpers.withMessage("can only be integers", integer),
      },
      section: {
        required: helpers.withMessage("is required", required),
        integer: helpers.withMessage("can only be integers", integer),
      },
      roll_no: {
        required: helpers.withMessage("is required", required),
        integer: helpers.withMessage("can only be integers", integer),
        maxValue: helpers.withMessage("maximum 200", maxValue(200)),
      },
    };
  },

  computed: {
    standards() {
      return this.$store.getters["standard/allResources"];
    },
    sections() {
      return this.$store.getters["section/allResources"];
    },
  },

  created() {
    this.fetchStandards();
    this.fetchSections();
  },

  methods: {
    setModalState(state) {
      this.modalState = state;
    },

    loader(message) {
      const loading = loadingController.create({
        message: message,
        backdropDismiss: false,
      });
      return loading;
    },

    alerter(header, message, buttons = ["close"]) {
      const alert = alertController.create({
        header: header,
        message: message,
        buttons: buttons,
      });

      return alert;
    },

    setErrorResponse(error) {
      if (error.response) {
        this.xhrError.header = error.response.data.header;
        this.xhrError.message = error.response.data.message;
      } else if (error.request) {
        this.xhrError.header = error.message || error.request;
        this.xhrError.message = "Please check your connection and try again";
      } else {
        this.xhrError.header = error.message;
        this.xhrError.message = "Something went wrong. Try again later";
      }
    },

    async confirmUpdate() {
      const header = `Confirm`;
      const message = "Are you sure you want to re-enlist student and save changes?";
      const buttons = [
        {
          text: "Cancel",
          role: "cancel",
          cssClass: "secondary",
        },
        {
          text: "Okay",
          handler: () => {
            this.updateStudent();
          },
        },
      ];
      const alertDialog = await this.alerter(header, message, buttons);

      alertDialog.present();
    },

    async updateStudent() {
      this.v$.$touch();
      if (this.v$.$error) {
        this.validationError = true;
        return;
      }

      let data = {
        id: this.id,
        standard_id: this.standard,
        section_id: this.section,
        roll_no: this.roll_no,
      };

      const spinner = await this.loader("Saving...");
      spinner.present();

      try {
        await this.$store.dispatch("studentTrashed/updateResource", data);
        spinner.dismiss();

        this.name = null;
        this.$emit("closeModal");
      } catch (error) {
        spinner.dismiss();

        this.setErrorResponse(error);

        const alertDialog = await this.alerter(
          this.xhrError.header,
          this.xhrError.message
        );

        alertDialog.present();

        this.password = null;
        this.validationError = null;
      }
    },

    async fetchStandards() {
      await this.$store.dispatch("standard/allResources");
      this.standard =
        this.resource.student_trashed.section_standard.standard.id;
    },

    async fetchSections() {
      await this.$store.dispatch("section/allResources");
      this.section = this.resource.student_trashed.section_standard.section.id;
    },
  },
};
</script>